import { useState, useRef, useEffect } from "react";
import { Section, Text } from "@/components";
import { useApp } from "@/hooks";
import "./HowItWorksSection.scss";
import { ProgressStep } from "./components";
const Vimeo = require('@u-wave/react-vimeo').default;

// Data for the steps to enable mapping
const steps = [
  {
    title: "Create an IntelliRoom™",
    description:
      "Spin up a dedicated IntelliRoom™ for each company you’re evaluating.",
    videoSrc: "https://vimeo.com/1103545859",
    estimatedTime: "00:10",
  },
  {
    title: "Upload the Data Room",
    description: "Add PDFs, Excel files, and other documents.",
    videoSrc: "https://vimeo.com/1103547842",
    estimatedTime: "00:08",
  },
  {
    title: "Generate DD Checklists",
    description:
      "Surface key factors driving or eroding investment value",
    videoSrc: "https://vimeo.com/1103548153",
    estimatedTime: "00:18",
  },
  {
    title: "AI Answers",
    description:
      "Use our private LLM to research, refine thesis, and draft memos.",
    videoSrc: "https://vimeo.com/1103548645",
    estimatedTime: "00:06",
  },
];

export const HowItWorksSection = (): JSX.Element => {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [videoProgress, setVideoProgress] = useState<number>(0); // Type for state
  const videoRef = useRef<any>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // State to hold progress for all steps. Initialize all to 0.
  const [allStepsProgress, setAllStepsProgress] = useState<number[]>(
    steps.map(() => 0)
  );

  const { screen } = useApp();

  // Effect to load the correct video when currentStepIndex changes
  useEffect(() => {
    if (!isPlaying) return;

    const videoElement = videoRef.current;
    if (!videoElement) return;
    if (videoElement) {
      // Optionally, auto-play the next video
      if (!isPlaying) {
        setVideoProgress(0); // If not auto-playing, reset progress for new video
      }
    }

    // Also reset the current step's visual progress to 0 when moving to it
    setAllStepsProgress((prev) => {
      const newProgress = steps.map(() => 0);
      if (currentStepIndex > 0) {
        // If not the very first step, make sure previous step is 100%
        for (let i = 0; i < currentStepIndex; i++) {
          newProgress[i] = 100; // Reset all subsequent steps
        }
      } else {
        newProgress[currentStepIndex] = 0; // Reset current step's progress
      }
      return newProgress;
    });
  }, [currentStepIndex, isPlaying]);

  // Effect for handling video time updates
  // Type for event handlers
  const handleTimeUpdate = (currentTime: any) => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (currentTime.duration) {
      const percentage = currentTime.percent * 100;
      setVideoProgress(percentage);
      setAllStepsProgress((prev) => {
        const newProgress = [...prev];
        newProgress[currentStepIndex] = percentage;
        return newProgress;
      });
    }
  };

  const handleVideoEnded = () => {
    setAllStepsProgress((prev) => {
      const newProgress = [...prev];
      newProgress[currentStepIndex] = 100;
      return newProgress;
    });

    setIsPlaying(true);
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prevIndex) => prevIndex + 1);
    } else {
      setCurrentStepIndex(0);
    }
  };

  const handleCanPlay = () => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isPlaying && videoElement.paused) {
      videoElement.muted = true; // Mute the video to allow autoplay
      // videoElement.play().catch((error) => console.log("Autoplay prevented:", error));
    }
  };

  const handleStepClick = (index: number) => {
    // Type for index
    if (index !== currentStepIndex) {
      setCurrentStepIndex(index);
      setIsPlaying(true);
    }
  };

  // Simulate handleStepClick(0) when section enters viewport
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsPlaying(true);
            setAllStepsProgress(steps.map(() => 0)); // Reset all steps progress
            handleStepClick(0);
          }
        });
      },
      { threshold: 0.3 } // Adjust as needed
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <Section
      id="how-it-works-section"
      ref={sectionRef}
      className="flex-col mx-0 !px-4 pb-[52px] gap-[30px] md:gap-[40px] bg-[#0E0E0E]"
    >
      <div className="flex flex-col items-center mt-1 md:mt-3">
        <div className="flex gap-0 md:gap-1">
          <Text.Heading
            size="xl"
            className="base-title text-white text-center"
          >
            How
          </Text.Heading>
          &nbsp;
          <Text.Heading
            size="xl"
            className="base-title how-it-works-title1 text-center"
          >
            IntelliRoom™
          </Text.Heading>
          &nbsp;
          <Text.Heading
            size="xl"
            className="base-title how-it-works-title text-center"
          >
            Works
          </Text.Heading>
        </div>
        <Text.Description
          size="sm"
          className="max-w-[273px] md:max-w-[530px] font-light !text-[#C7C7C7] md:!text-[#FEFEFE] text-center leading-[20px] md:font-[300] font-inter mt-3 md:mt-1"
        >
          Step-by-Step DD Process For Any Private Market Investment
        </Text.Description>
      </div>

      <div className="max-w-[1148px] mx-auto grid grid-cols-1 lg:grid-cols-4 gap-5 lg:mt-[20px]">
        {steps.map((step, index) => (
          <ProgressStep
            key={`video-stepper-${index}`}
            stepIndex={index}
            title={step.title}
            description={step.description}
            videoSrc={step.videoSrc}
            videoRef={["", "sm", "md"].includes(screen) ? videoRef : null}
            progressPercentage={allStepsProgress[index]}
            isActive={index === currentStepIndex}
            onClick={() => handleStepClick(index)}
            onTimeUpdate={handleTimeUpdate}
            onEnd={handleVideoEnded}
            onReady={handleCanPlay}
          />
        ))}
      </div>

      {!["", "sm", "md"].includes(screen) && (
        <div className="w-full max-w-[1148px]">
          <div className="w-full p-10 tutorial-video-section relative">
            <div className="w-full h-full tutorial-video-container relative">
              {steps.map((step, index) => (
                <Vimeo
                  key={index}
                  ref={index === currentStepIndex ? videoRef : undefined}
                  video={step.videoSrc}
                  muted
                  responsive
                  autoplay={true}
                  play={true}
                  paused={index !== currentStepIndex}
                  controls={false}
                  onTimeUpdate={handleTimeUpdate}
                  onEnd={handleVideoEnded}
                  onReady={handleCanPlay}
                  className={`video-display rounded-[10px] overflow-hidden absolute top-0 left-0 w-full h-full transition-opacity duration-500 ${
                    index === currentStepIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};
